import { base64FromInt16, downsampledMono, int16From, levelFromFloat, rawRms } from './pcm.js';

/**
 * The realtime transcription session: microphone blocks up one authenticated socket, transcript
 * events back down it, and two transcript states the whole way — the committed text and the one
 * interim segment, which is **replaced** on every partial and folded into committed exactly once.
 * Nothing here ever concatenates partial events, which is the defect this file exists to make
 * impossible.
 *
 * Audio never persists: each block is converted, sent and dropped; the microphone is released on
 * every exit including thrown ones; cancel discards audio and words without sending anything more.
 *
 * **Capture starts at the press, not at "ready"** (2026-09-21). The microphone used to open only
 * once the relay had set its provider session up — measured at 0.4 to 0.8 s, plus the microphone's
 * own start — so whatever a person said in that window was never captured at all, and it read as
 * the transcriber missing the first word. The microphone and the socket now open together; what is
 * heard before the relay is ready is held in memory, bounded, and sent the moment it is. A short
 * hold released inside that window is committed once the relay is ready instead of being dropped.
 *
 * Every browser and network surface is injected, so all of this behaviour runs under test with a
 * scripted socket and a scripted pump. The real seams are one file that only passes browser objects
 * through (`live-seams.ts`).
 */

export interface LiveSocketLike {
  send(data: string): void;
  close(): void;
  addEventListener(type: 'open' | 'error', listener: () => void): void;
  addEventListener(type: 'message', listener: (event: { readonly data: unknown }) => void): void;
  addEventListener(type: 'close', listener: (event: { readonly code: number }) => void): void;
}

/** A running microphone pipeline; stopping it releases the microphone and the audio graph. */
export interface PcmPumpLike {
  stop(): void;
}

export interface LiveTranscriptSeams {
  readonly openSocket: (sessionId: string) => LiveSocketLike;
  /** Opens the microphone and calls back with float blocks at the given rate until stopped. */
  readonly openPump: (
    onBlock: (samples: Float32Array, sampleRate: number) => void,
  ) => Promise<PcmPumpLike>;
  readonly setTimer: (handler: () => void, ms: number) => ReturnType<typeof setTimeout>;
  readonly clearTimer: (timer: ReturnType<typeof setTimeout>) => void;
}

export type LiveEnd =
  | { readonly kind: 'committed'; readonly transcript: string }
  | { readonly kind: 'cancelled' }
  /** The realtime path is not available at all — the caller should fall back to HTTP capture. */
  | { readonly kind: 'unavailable'; readonly code: string }
  | { readonly kind: 'failed'; readonly code: string };

export interface LiveTranscriptHandlers {
  /** The full committed transcript so far (segments joined); never shrinks. */
  readonly onCommitted: (text: string) => void;
  /** The current interim segment; replaces the previous interim entirely. Empty clears it. */
  readonly onInterim: (text: string) => void;
  /** Real microphone loudness, one value per captured block. */
  readonly onLevel: (level: number) => void;
  /** The socket is open and the provider is ready; recording is genuinely live from here. */
  readonly onLive: () => void;
  readonly onEnded: (end: LiveEnd) => void;
}

export interface LiveTranscriptSession {
  stop(): void;
  cancel(): void;
}

/**
 * Hands-free voice mode (founder-directed): the session commits itself once the person has spoken
 * and then stayed quiet. Silence is measured on the sample clock — the audio's own duration —
 * never the wall clock, so the rule is deterministic and testable. Dictation into the composer
 * passes no autoCommit and keeps its explicit stop button.
 */
export interface AutoCommitRule {
  /** How much captured audio must stay silent before the session commits itself. */
  readonly silenceMs: number;
  /** Real speech must have been heard first; a session of pure silence never commits itself. */
  readonly minSpeechMs: number;
  /**
   * Raw-RMS gate with hysteresis: speech begins at or above `speechRms`, and only ends below
   * `releaseRms` — the band between keeps its previous classification, so breathy trailing words
   * do not flap the detector. Both are on the unshaped scale, where room noise stays low.
   */
  readonly speechRms: number;
  readonly releaseRms: number;
  /**
   * Adaptive guard: speech must also exceed the tracked noise floor by this ratio, so a fan, a
   * street or microphone gain cannot masquerade as a person for ever.
   */
  readonly floorRatio: number;
  /** No utterance runs for ever: at this length the session commits what it has. */
  readonly maxUtteranceMs: number;
}

/**
 * 100 ms of audio per frame at 16 kHz — what the transcription provider's own documentation asks
 * for ("send audio in chunks of 100ms"). It was 250 ms until 2026-09-21, which held the first frame
 * back a quarter of a second and made the live words arrive in coarser steps than they needed to.
 */
export const LIVE_FRAME_SAMPLES = 1600;
const SAMPLES_PER_FRAME = LIVE_FRAME_SAMPLES;
/**
 * The most that is held while the relay is still being set up: ten seconds. A wait that long is a
 * broken connection rather than a slow one; the bound exists so memory is, and it keeps the
 * beginning of what was said rather than the end, because the beginning is what used to be lost.
 */
const MAX_HELD_FRAMES = 100;
/** How long stop waits for the provider's final committed segment before giving up. */
const COMMIT_DEADLINE_MS = 6000;

export function startLiveTranscript(
  seams: LiveTranscriptSeams,
  sessionId: string,
  handlers: LiveTranscriptHandlers,
  autoCommit?: AutoCommitRule,
): LiveTranscriptSession {
  const socket = seams.openSocket(sessionId);
  const committedParts: string[] = [];
  let pending = new Float32Array(0);
  /** Whole frames heard before the relay was ready, oldest first. */
  let held: Float32Array[] = [];
  let pump: PcmPumpLike | null = null;
  let phase: 'connecting' | 'live' | 'stopping' | 'ended' = 'connecting';
  /** The relay has said it is ready; going live also needs the microphone. */
  let relayReady = false;
  /** The person let go while the relay was still being set up, with something already said. */
  let releasedEarly = false;
  let commitDeadline: ReturnType<typeof setTimeout> | null = null;

  const stopPump = (): void => {
    pump?.stop();
    pump = null;
  };
  const releaseAudio = (): void => {
    stopPump();
    pending = new Float32Array(0);
    held = [];
  };

  const end = (outcome: LiveEnd): void => {
    if (phase === 'ended') return;
    phase = 'ended';
    if (commitDeadline !== null) seams.clearTimer(commitDeadline);
    releaseAudio();
    try {
      socket.close();
    } catch {
      /* already closed */
    }
    handlers.onEnded(outcome);
  };

  const sendFrame = (samples: Float32Array): void => {
    if (samples.length === 0) return;
    socket.send(
      JSON.stringify({ type: 'chunk', audioBase64: base64FromInt16(int16From(samples)) }),
    );
  };

  let speechMs = 0;
  let silenceMs = 0;
  let utteranceMs = 0;
  let inSpeech = false;
  let noiseFloor = 0.006;

  /** A whole frame: on the wire once live, held until then. */
  const emitFrame = (frame: Float32Array): void => {
    if (phase === 'live') sendFrame(frame);
    else if (held.length < MAX_HELD_FRAMES) held.push(frame);
  };

  const onBlock = (samples: Float32Array, sampleRate: number): void => {
    if (phase !== 'live' && phase !== 'connecting') return;
    // The meter moves from the first block, ready or not: it is how a person sees they are heard.
    handlers.onLevel(levelFromFloat(samples));
    // The hands-free rule measures silence on a live session only; while the relay is being set
    // up there is nothing to commit, and a pause then must not end a session that has not begun.
    if (autoCommit !== undefined && phase === 'live') {
      const blockMs = (samples.length / sampleRate) * 1000;
      utteranceMs += blockMs;
      const rms = rawRms(samples);
      // The floor learns only from quiet blocks, slowly, and never sinks to zero.
      if (rms < autoCommit.speechRms) noiseFloor = Math.max(0.003, noiseFloor * 0.95 + rms * 0.05);
      const speechGate = Math.max(autoCommit.speechRms, noiseFloor * autoCommit.floorRatio);
      const releaseGate = Math.max(autoCommit.releaseRms, noiseFloor * 1.5);
      if (rms >= speechGate) inSpeech = true;
      else if (rms <= releaseGate) inSpeech = false;
      // Between the gates the previous classification stands (hysteresis).
      if (inSpeech) {
        speechMs += blockMs;
        silenceMs = 0;
      } else if (speechMs >= autoCommit.minSpeechMs) {
        silenceMs += blockMs;
        if (silenceMs >= autoCommit.silenceMs) {
          // The pause is the person's full stop: flush, commit, and let onEnded carry the words.
          session.stop();
          return;
        }
      }
      if (speechMs >= autoCommit.minSpeechMs && utteranceMs >= autoCommit.maxUtteranceMs) {
        session.stop();
        return;
      }
    }
    const resampled = downsampledMono(samples, sampleRate);
    const merged = new Float32Array(pending.length + resampled.length);
    merged.set(pending, 0);
    merged.set(resampled, pending.length);
    let offset = 0;
    while (merged.length - offset >= SAMPLES_PER_FRAME) {
      emitFrame(merged.slice(offset, offset + SAMPLES_PER_FRAME));
      offset += SAMPLES_PER_FRAME;
    }
    pending = merged.slice(offset);
  };

  /** Read through a function: the phase moves while an await is pending, which a narrowed local cannot see. */
  const hasEnded = (): boolean => phase === 'ended';
  const wasReleasedEarly = (): boolean => releasedEarly;

  /** Both halves are there: send what was held, in order, then stream as it always did. */
  const goLive = (): void => {
    if (phase !== 'connecting' || !relayReady) return;
    if (pump === null && !releasedEarly) return;
    phase = 'live';
    for (const frame of held) sendFrame(frame);
    held = [];
    handlers.onLive();
    if (releasedEarly) session.stop();
  };

  // The microphone opens with the socket, not after it.
  void (async () => {
    try {
      const opened = await seams.openPump(onBlock);
      if (hasEnded() || wasReleasedEarly()) {
        // Ended, or let go, before the microphone answered: it closes again at once.
        opened.stop();
        return;
      }
      pump = opened;
      goLive();
    } catch {
      // The realtime path could not open its microphone, which says nothing about the recorder: it
      // needs no worklet and no audio graph. So this is the path being unavailable, and the caller
      // falls back to it — the person still speaks. Reported as a failure until 2026-09-13, which
      // left someone whose worklet was blocked with an error and no way to talk at all.
      end({ kind: 'unavailable', code: 'microphone_unavailable' });
    }
  })();

  socket.addEventListener('message', (event) => {
    if (typeof event.data !== 'string') return;
    let frame: unknown;
    try {
      frame = JSON.parse(event.data);
    } catch {
      return;
    }
    if (typeof frame !== 'object' || frame === null) return;
    const message = frame as Record<string, unknown>;
    if (message['type'] === 'ready') {
      if (phase !== 'connecting') return;
      relayReady = true;
      goLive();
      return;
    }
    if (message['type'] === 'partial' && typeof message['text'] === 'string') {
      handlers.onInterim(message['text']);
      return;
    }
    if (message['type'] === 'committed' && typeof message['text'] === 'string') {
      if (message['text'].trim().length > 0) committedParts.push(message['text'].trim());
      handlers.onInterim('');
      handlers.onCommitted(committedParts.join(' '));
      if (phase === 'stopping') end({ kind: 'committed', transcript: committedParts.join(' ') });
      return;
    }
    if (message['type'] === 'error' && typeof message['code'] === 'string') {
      end(
        phase !== 'connecting'
          ? { kind: 'failed', code: message['code'] }
          : releasedEarly
            ? { kind: 'cancelled' }
            : { kind: 'unavailable', code: message['code'] },
      );
    }
  });

  socket.addEventListener('close', (event) => {
    if (phase === 'ended') return;
    // A close before anything went live means the path is not available (4503, 4401, 4429…);
    // afterwards it is a mid-recording failure the caller can retry or fall back from.
    if (phase === 'connecting') {
      // `unavailable` starts the recorded fallback, which is wrong for someone who already let go.
      end(
        releasedEarly
          ? { kind: 'cancelled' }
          : { kind: 'unavailable', code: `ws_${String(event.code)}` },
      );
      return;
    }
    if (phase === 'stopping') {
      // The provider closed after commit without a final segment: what was committed stands.
      end({ kind: 'committed', transcript: committedParts.join(' ') });
      return;
    }
    end({ kind: 'failed', code: `ws_${String(event.code)}` });
  });
  socket.addEventListener('error', () => {
    if (phase === 'connecting')
      end(releasedEarly ? { kind: 'cancelled' } : { kind: 'unavailable', code: 'ws_error' });
  });

  const session: LiveTranscriptSession = {
    stop(): void {
      if (phase !== 'live') {
        if (phase !== 'connecting' || releasedEarly) return;
        // Let go while the relay was still being set up. With nothing heard there is nothing to
        // keep. With something heard, the microphone is released now and the words are committed
        // the moment the relay is ready — a short hold used to vanish here without a trace.
        if (held.length === 0 && pending.length === 0) {
          end({ kind: 'cancelled' });
          return;
        }
        releasedEarly = true;
        stopPump();
        goLive();
        return;
      }
      phase = 'stopping';
      // Flush what the frame boundary was still holding, then ask the provider to finalize.
      sendFrame(pending);
      releaseAudio();
      socket.send(JSON.stringify({ type: 'commit' }));
      commitDeadline = seams.setTimer(() => {
        end({ kind: 'committed', transcript: committedParts.join(' ') });
      }, COMMIT_DEADLINE_MS);
    },
    cancel(): void {
      if (phase === 'ended') return;
      try {
        socket.send(JSON.stringify({ type: 'cancel' }));
      } catch {
        /* socket may already be closed */
      }
      end({ kind: 'cancelled' });
    },
  };
  return session;
}
